import { useMemo } from 'react';
import { buildBacklinkIndex, resolveKind, type Adventure } from '@gm-binder/shared';
import { useRefNav } from '../../lib/refNav';
import styles from './Backlinks.module.css';

export interface BacklinksProps {
  adventure: Adventure;
  /** The referenced entity, as a `"kind:id"` reference. */
  targetRef: string;
  title?: string;
}

/**
 * "Referenced by" panel: lists entities that link to `targetRef` via explicit `kind:id`
 * references or `[[wiki-links]]`. Renders nothing when there are no incoming links, so it's
 * safe to drop into any detail view.
 */
export function Backlinks({ adventure, targetRef, title = 'Referenziato da' }: BacklinksProps) {
  const refNav = useRefNav();
  const items = useMemo(
    () => buildBacklinkIndex(adventure).get(targetRef) ?? [],
    [adventure, targetRef],
  );

  if (items.length === 0) return null;

  const fieldLabel = (kind: string, field: string) =>
    resolveKind(kind, adventure.kinds as Parameters<typeof resolveKind>[1])?.fields.find(
      (f) => f.key === field,
    )?.label ?? field;

  return (
    <section className={styles.backlinks}>
      <h4 className={styles.heading}>{title}</h4>
      <ul className={styles.list}>
        {items.map((b) => (
          <li key={`${b.from.ref}:${b.field}`}>
            <button
              type="button"
              className={styles.link}
              onClick={() => refNav?.navigateRef(b.from.ref)}
              disabled={!refNav}
            >
              <span className={styles.icon} aria-hidden>
                {b.from.icon ?? '🔗'}
              </span>
              <span className={styles.label}>{b.from.label}</span>
            </button>
            <span className={styles.field}>{fieldLabel(b.from.kind, b.field)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
